// ─── JAL CELL ────────────────────────────────────────────────────────────────
// Composant pour la colonne JAL dans la table Opérations admin.
// - Si pas de JAL : zone de drop d'une attestation PDF
// - Si N JAL : badge cliquable qui ouvre un popover liste
// - Drop : upload + Claude Vision extraction → modale validation → POST /api/jal

import React, { useState, useRef } from 'react';
import api from '../services/api';

const D = {
  bg:'#0A0A0F', s1:'#0F0F17', s2:'#15151F', s3:'#1A1A26',
  b1:'rgba(255,255,255,0.06)', b2:'rgba(255,255,255,0.12)',
  tx:'#E8E6E0', tx2:'rgba(232,230,224,0.65)', tx3:'rgba(232,230,224,0.4)',
  or:'#D4A843',
  green:'#4FBF7F', red:'#EF5B5B', amber:'#F59E0B', blue:'#5B8DEF',
};

const card = { background:D.s2, border:`1px solid ${D.b1}`, borderRadius:'8px' };
const inp = { width:'100%', padding:'7px 11px', background:D.s2, border:`1px solid ${D.b1}`, borderRadius:'6px', fontSize:'12px', color:D.tx, outline:'none', fontFamily:'DM Sans, sans-serif' };
const btn = { background:D.or, color:'#09090E', border:'none', padding:'7px 16px', borderRadius:'6px', fontSize:'11px', fontWeight:500, cursor:'pointer', fontFamily:'DM Sans, sans-serif' };
const btnGh = { background:'transparent', color:D.tx2, border:`1px solid ${D.b1}`, padding:'7px 14px', borderRadius:'6px', fontSize:'11px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' };
const lbl = { fontSize:'10px', textTransform:'uppercase', letterSpacing:'0.05em', color:D.tx3, marginBottom:'4px' };

const STATUT_LABELS = {
  en_attente: 'En attente',
  en_cours: 'En cours',
  publie: 'Publié',
  erreur: 'Erreur',
};
const STATUT_COLORS = {
  en_attente: D.amber,
  en_cours: D.blue,
  publie: D.green,
  erreur: D.red,
};

export default function JalCell({ dossier, jalList = [], onChanged }) {
  const [dragging, setDragging]   = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showList, setShowList]   = useState(false);
  const [extractData, setExtractData] = useState(null);  // {storage_path, journal, ...}
  const fileRef = useRef(null);

  const hasJal = jalList && jalList.length > 0;

  const handleFile = async (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      alert('Format non supporté : PDF uniquement.');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('dossier_id', dossier.id);
      fd.append('file', file);
      const r = await api.post('/api/jal/extract-from-pdf', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Ouvre la modale de validation pré-remplie
      setExtractData({
        storage_path: r.data.storage_path,
        journal: r.data.journal || '',
        type_annonce: r.data.type_annonce || '',
        date_parution: r.data.date_parution || '',
        reference_publication: r.data.reference_publication || '',
        cout_ht: '',
        publie_par: 'lexback',
        statut: 'publie',
      });
    } catch (e) {
      alert('Erreur extraction : ' + (e.response?.data?.detail || e.message));
    } finally { setUploading(false); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };

  const handleCreate = async (formValues) => {
    try {
      const payload = {
        dossier_id: dossier.id,
        journal: formValues.journal || null,
        type_annonce: formValues.type_annonce || null,
        publie_par: formValues.publie_par,
        statut: formValues.statut,
        reference_publication: formValues.reference_publication || null,
        date_parution: formValues.date_parution || null,
        cout_ht: formValues.cout_ht ? parseFloat(formValues.cout_ht) : null,
        attestation_url: extractData.storage_path,
      };
      await api.post('/api/jal', payload);
      setExtractData(null);
      if (onChanged) onChanged();
    } catch (e) {
      alert('Erreur création : ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleDelete = async (jalId) => {
    if (!window.confirm('Supprimer cette annonce ?')) return;
    try {
      await api.delete(`/api/jal/${jalId}`);
      if (onChanged) onChanged();
    } catch (e) {
      alert('Erreur suppression : ' + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <>
      <div
        onClick={e => {
          e.stopPropagation();
          if (hasJal) setShowList(true);
          else fileRef.current?.click();
        }}
        onDragOver={e => { e.preventDefault(); e.stopPropagation(); setDragging(true); }}
        onDragLeave={e => { e.preventDefault(); setDragging(false); }}
        onDrop={handleDrop}
        style={{
          minWidth: '70px', padding: '4px 8px', borderRadius: '5px',
          border: dragging ? `1px dashed ${D.or}` : `1px dashed ${hasJal ? 'transparent' : D.b2}`,
          background: dragging ? 'rgba(212,168,67,0.08)' : 'transparent',
          fontSize: '11px', textAlign: 'center', cursor: 'pointer',
          color: hasJal ? D.tx : D.tx3,
          transition: 'all 0.12s',
        }}
        title={hasJal ? 'Voir les JAL' : 'Glisser un PDF d\'attestation'}
      >
        {uploading ? (
          <span style={{ color: D.or }}>↑ Analyse…</span>
        ) : hasJal ? (
          <span style={{ display: 'inline-flex', gap: '4px', alignItems: 'center' }}>
            <span style={{ width:'6px', height:'6px', borderRadius:'50%', background: STATUT_COLORS[jalList[0].statut] }} />
            {jalList.length} {jalList.length > 1 ? 'JAL' : 'JAL'}
          </span>
        ) : (
          <span style={{ opacity: 0.6 }}>+ PDF</span>
        )}
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="application/pdf"
        style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; e.target.value=''; handleFile(f); }}
      />

      {/* Modale validation après extraction */}
      {extractData && (
        <JalValidateModal
          extractData={extractData}
          onCancel={() => setExtractData(null)}
          onCreate={handleCreate}
        />
      )}

      {/* Popover liste des JAL existants */}
      {showList && (
        <JalListModal
          dossier={dossier}
          jalList={jalList}
          onClose={() => setShowList(false)}
          onDelete={handleDelete}
          onAddNew={() => { setShowList(false); fileRef.current?.click(); }}
        />
      )}
    </>
  );
}


// ─── Modale validation après extraction Claude Vision ───────────────────────
function JalValidateModal({ extractData, onCancel, onCreate }) {
  const [form, setForm] = useState(extractData);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await onCreate(form);
    } finally {
      setSaving(false);
    }
  };

  const wasExtracted = (key) => extractData[key] && extractData[key] !== '';

  return (
    <div onClick={onCancel} style={{
      position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:1100,
      display:'flex', alignItems:'center', justifyContent:'center', padding:'20px',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        ...card, padding:'24px', width:'520px', maxWidth:'100%', maxHeight:'90vh', overflow:'auto',
      }}>
        <div style={{ fontFamily:'Syne, sans-serif', fontSize:'15px', fontWeight:600, color:D.tx, marginBottom:'4px' }}>
          Nouvelle annonce légale
        </div>
        <div style={{ fontSize:'11px', color:D.tx3, marginBottom:'18px' }}>
          Champs pré-remplis depuis l'attestation. Vérifie et complète le coût HT.
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
          <div>
            <div style={lbl}>Journal {wasExtracted('journal') && <span style={{ color:D.green }}>· IA</span>}</div>
            <input style={inp} value={form.journal} onChange={e => setForm({...form, journal:e.target.value})} placeholder="Les Échos" />
          </div>
          <div>
            <div style={lbl}>Type d'annonce {wasExtracted('type_annonce') && <span style={{ color:D.green }}>· IA</span>}</div>
            <input style={inp} value={form.type_annonce} onChange={e => setForm({...form, type_annonce:e.target.value})} placeholder="Constitution" />
          </div>
          <div>
            <div style={lbl}>Date de parution {wasExtracted('date_parution') && <span style={{ color:D.green }}>· IA</span>}</div>
            <input style={inp} type="date" value={form.date_parution || ''} onChange={e => setForm({...form, date_parution:e.target.value})} />
          </div>
          <div>
            <div style={lbl}>Référence publication {wasExtracted('reference_publication') && <span style={{ color:D.green }}>· IA</span>}</div>
            <input style={inp} value={form.reference_publication} onChange={e => setForm({...form, reference_publication:e.target.value})} placeholder="N° 12345" />
          </div>
          <div>
            <div style={lbl}>Coût HT (€) <span style={{ color:D.amber }}>· manuel</span></div>
            <input style={inp} type="number" step="0.01" value={form.cout_ht} onChange={e => setForm({...form, cout_ht:e.target.value})} placeholder="145.00" />
          </div>
          <div>
            <div style={lbl}>Publié par</div>
            <select style={inp} value={form.publie_par} onChange={e => setForm({...form, publie_par:e.target.value})}>
              <option value="lexback">Lexback</option>
              <option value="client">Client</option>
            </select>
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <div style={lbl}>Statut</div>
            <select style={inp} value={form.statut} onChange={e => setForm({...form, statut:e.target.value})}>
              <option value="publie">Publié</option>
              <option value="en_attente">En attente</option>
              <option value="en_cours">En cours</option>
              <option value="erreur">Erreur</option>
            </select>
          </div>
        </div>

        <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end', marginTop:'20px' }}>
          <button onClick={onCancel} style={btnGh} disabled={saving}>Annuler</button>
          <button onClick={handleSubmit} style={btn} disabled={saving}>
            {saving ? 'Création…' : 'Créer l\'annonce'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── Popover liste des JAL ───────────────────────────────────────────────────
function JalListModal({ dossier, jalList, onClose, onDelete, onAddNew }) {
  return (
    <div onClick={onClose} style={{
      position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:1100,
      display:'flex', alignItems:'center', justifyContent:'center', padding:'20px',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        ...card, padding:'24px', width:'600px', maxWidth:'100%', maxHeight:'90vh', overflow:'auto',
      }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'16px' }}>
          <div>
            <div style={{ fontFamily:'Syne, sans-serif', fontSize:'15px', fontWeight:600, color:D.tx }}>
              Annonces légales
            </div>
            <div style={{ fontSize:'11px', color:D.tx3, marginTop:'2px' }}>
              {dossier.societe_nom} · {jalList.length} annonce{jalList.length > 1 ? 's' : ''}
            </div>
          </div>
          <button onClick={onAddNew} style={btn}>+ Ajouter</button>
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
          {jalList.map(j => (
            <div key={j.id} style={{
              border:`1px solid ${D.b1}`, borderRadius:'8px', padding:'12px 14px',
              display:'grid', gridTemplateColumns:'1fr auto', gap:'10px',
            }}>
              <div>
                <div style={{ display:'flex', gap:'8px', alignItems:'center', marginBottom:'6px' }}>
                  <span style={{
                    width:'8px', height:'8px', borderRadius:'50%', background: STATUT_COLORS[j.statut],
                  }} />
                  <span style={{ fontSize:'12px', color:D.tx, fontWeight:500 }}>
                    {j.journal || 'Journal non précisé'}
                  </span>
                  <span style={{
                    fontSize:'9px', textTransform:'uppercase', letterSpacing:'0.05em',
                    background: j.publie_par === 'lexback' ? 'rgba(212,168,67,0.12)' : 'rgba(91,141,239,0.12)',
                    color: j.publie_par === 'lexback' ? D.or : D.blue,
                    padding:'2px 6px', borderRadius:'3px', fontWeight:600,
                  }}>
                    {j.publie_par}
                  </span>
                </div>
                <div style={{ fontSize:'11px', color:D.tx2, lineHeight:1.5 }}>
                  {j.type_annonce && <>Type : <span style={{ color:D.tx }}>{j.type_annonce}</span> · </>}
                  {j.date_parution && <>Parution : <span style={{ color:D.tx }}>{new Date(j.date_parution).toLocaleDateString('fr-FR')}</span> · </>}
                  {j.reference_publication && <>Réf : <span style={{ color:D.tx }}>{j.reference_publication}</span> · </>}
                  Statut : <span style={{ color: STATUT_COLORS[j.statut] }}>{STATUT_LABELS[j.statut]}</span>
                </div>
                {j.cout_ht !== null && j.cout_ht !== undefined && (
                  <div style={{ fontSize:'11px', color:D.tx3, marginTop:'4px' }}>
                    Coût : <span style={{ color:D.tx }}>{j.cout_ht.toFixed(2)} € HT</span>
                  </div>
                )}
                {j.attestation_url && (
                  <button
                    onClick={async () => {
                      try {
                        const r = await api.get(`/api/jal/${j.id}/attestation-url`);
                        if (r.data?.url) window.open(r.data.url, '_blank');
                      } catch (e) {
                        alert('Impossible d\'ouvrir l\'attestation');
                      }
                    }}
                    style={{
                      ...btnGh, padding:'3px 8px', fontSize:'10px', marginTop:'6px',
                    }}
                  >Voir l'attestation</button>
                )}
              </div>
              <div>
                <button
                  onClick={() => onDelete(j.id)}
                  style={{
                    background:'transparent', color:D.red, border:`1px solid rgba(239,91,91,0.25)`,
                    padding:'4px 10px', borderRadius:'5px', fontSize:'10px', cursor:'pointer',
                  }}
                >Suppr</button>
              </div>
            </div>
          ))}
        </div>

        <div style={{ display:'flex', justifyContent:'flex-end', marginTop:'18px' }}>
          <button onClick={onClose} style={btnGh}>Fermer</button>
        </div>
      </div>
    </div>
  );
}
